import { useMemo } from 'react';
import { Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import RolodexEmpty from '@/components/RolodexEmpty';
import VendorCategoryPill from '@/components/VendorCategoryPill';
import { matchesVendorQuery, primaryEmail, type VendorOption } from '@/lib/vendor-search';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

/**
 * The checkbox grid half of vendor picking, lifted out of `ComposePage` so the
 * surface it is drawn on stays the caller's choice. It sits in the side sheet
 * the composer opens from the recipient field's own row, and knows nothing
 * about that — it was an inline disclosure first and moved without a rewrite,
 * which is the property worth keeping. It owns no search input of its own — `query` is
 * the recipient field's text, filtered here the same way the field itself
 * matches, so there is one place to type and two ways to pick.
 */
export default function RolodexPicker({
  vendors,
  pending,
  selected,
  onToggle,
  query,
  category,
  categoryFilter,
  onCategoryFilterChange,
  disabledReason,
  atCap,
  onAddVendor,
}: {
  vendors: VendorOption[] | undefined;
  /** Query still in flight. `undefined` vendors also covers a failed fetch,
   *  which must not claim the rolodex is empty. */
  pending: boolean;
  selected: string[];
  onToggle: (vendorId: string, next: boolean) => void;
  /** The recipient field's text. This component owns no search input of its
   *  own — one place to type, two ways to pick. */
  query: string;
  /** The category being inquired about. Only ever read to keep the filter's
   *  current value nameable in the list of options. */
  category: string;
  /**
   * Which slice of the rolodex to show, held by the caller rather than here.
   *
   * Held here it was lost on every close: this component unmounts with the
   * surface around it — Radix renders `isOpen && children` for both the
   * disclosure this used to sit in and the sheet it sits in now — so local
   * state re-initialised to the inquired category on reopen. A planner who
   * widened to the whole rolodex to find a caterer lost that the moment she
   * shut the panel to look at her chips.
   */
  categoryFilter: string;
  /** Empty string means the whole rolodex. */
  onCategoryFilterChange: (next: string) => void;
  /** Why this vendor cannot be picked, or null if it can. Takes the vendor
   *  rather than its id; see the matching prop on `VendorRecipientField`. */
  disabledReason: (vendor: VendorOption) => string | null;
  /** MAX_BATCH_SIZE reached: unselected rows go disabled. */
  atCap: boolean;
  onAddVendor: () => void;
}) {
  /**
   * What the filter can be set to: the categories the rolodex actually uses,
   * plus the one being inquired about. Offering a category nobody is filed
   * under would be a control that can only ever empty the list; offering the
   * inquired one unconditionally is what keeps the opening state nameable
   * even when no vendor carries that tag yet.
   */
  const categoryOptions = useMemo(() => {
    const names = new Set((vendors ?? []).map((v) => v.primaryCategory).filter((c): c is string => !!c));
    if (category) names.add(category);
    return [...names].sort((a, b) => a.localeCompare(b));
  }, [vendors, category]);

  const filtered = useMemo(() => {
    return (vendors ?? []).filter(
      (v) => (!categoryFilter || v.primaryCategory === categoryFilter) && matchesVendorQuery(v, query),
    );
  }, [vendors, categoryFilter, query]);

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        {/* Opens on the category being inquired about and clears to the
            whole rolodex. The categories a planner files vendors under
            and the categories she inquires by are not the same list,
            and the vendor she wants is sometimes filed under neither. */}
        <Select
          value={categoryFilter || 'all'}
          onValueChange={(v) => onCategoryFilterChange(v === 'all' ? '' : v)}
        >
          <SelectTrigger className="h-9 w-44" aria-label="Filter by category">
            <SelectValue placeholder="All vendors" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All vendors</SelectItem>
            {categoryOptions.map((name) => (
              <SelectItem key={name} value={name}>
                {name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {/* The vendor a planner remembers mid-batch is the one they
            never got around to adding. Leaving to the rolodex and back
            loses the picks and the customization already typed, so the
            ask is forwarded instead — what opens, and what it is
            pre-filled with, is the caller's business. */}
        <Button variant="outline" size="sm" onClick={() => onAddVendor()}>
          <Plus className="size-3.5" />
          Add vendor
        </Button>
      </div>

      {/* `!vendors` covers a failed fetch as well as a pending one. The
          empty states below now make claims about the rolodex itself,
          and "your rolodex is empty" is a lie to tell someone whose
          request merely failed. */}
      {pending || !vendors ? null : filtered.length === 0 ? (
        vendors.length === 0 ? (
          // Nothing in the rolodex at all — the one state here whose cause is
          // not this component's filtering, which is why the words live in
          // their own file and the composer's card says them too.
          <RolodexEmpty />
        ) : categoryFilter ? (
          // The filter is what is excluding everyone, and the filter is
          // removable — so the recovery belongs on this screen rather
          // than as a trip to the rolodex. Name the category back
          // exactly as it was typed (no invented plural).
          <div className="space-y-3 py-6 text-center">
            <p className="text-sm text-muted-foreground">
              {query
                ? `No ${categoryFilter} vendors match your search.`
                : `No vendors tagged ${categoryFilter} yet.`}
            </p>
            <Button variant="outline" size="sm" onClick={() => onCategoryFilterChange('')}>
              {query ? 'Search all vendors' : 'Show all vendors'}
            </Button>
          </div>
        ) : (
          // Whole rolodex already in view — the search is just too
          // narrow. Nothing to fix in the rolodex, so no CTA here.
          <p className="py-6 text-center text-sm text-muted-foreground">
            No vendors match your search.
          </p>
        )
      ) : (
        <div className="grid gap-1 sm:grid-cols-2">
          {filtered.map((v) => {
            const reason = disabledReason(v);
            const disabled = reason !== null;
            const isSelected = selected.includes(v.id);
            return (
              <label
                key={v.id}
                className={`flex items-center gap-3 rounded-2xl px-3 py-2 text-sm ${
                  disabled
                    ? 'cursor-not-allowed opacity-50'
                    : 'cursor-pointer hover:bg-[var(--color-surface-container-high)]'
                }`}
              >
                <Checkbox
                  checked={isSelected}
                  disabled={disabled || (!isSelected && atCap)}
                  onCheckedChange={(checked) => onToggle(v.id, !!checked)}
                />
                <span className="min-w-0 flex-1">
                  <span className="block truncate font-medium">{v.businessName}</span>
                  <span className="block truncate text-xs text-muted-foreground">
                    {reason ?? primaryEmail(v) ?? 'No email address on file'}
                  </span>
                </span>
                {/* Only while the list spans categories. Under an active
                    filter every row would carry the same word, which is a
                    column of noise rather than a way to scan. */}
                {!categoryFilter && <VendorCategoryPill category={v.primaryCategory} />}
              </label>
            );
          })}
        </div>
      )}
    </div>
  );
}
